import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { FundAssetCashflow } from "@/types";

export function useCashflows(fundAssetId?: string) {
  return useQuery({
    queryKey: ["cashflows", fundAssetId ?? "all"],
    queryFn: async () => {
      const params = fundAssetId ? { fund_asset_id: fundAssetId } : undefined;
      const { data } = await apiClient.get<FundAssetCashflow[]>("/cashflows", { params });
      return data;
    },
  });
}

export const useAllCashflows = () => useCashflows(undefined);

export function useToggleCashflowReceived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, received }: { id: string; received: boolean }) => {
      const { data } = await apiClient.patch<FundAssetCashflow>(`/cashflows/${id}`, { received });
      return data;
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ["cashflows", row.fund_asset_id] });
      qc.invalidateQueries({ queryKey: ["cashflows", "all"] });
      qc.invalidateQueries({ queryKey: ["fund_assets"] });
    },
  });
}

export function useUpsertCashflows() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { fund_asset_id: string; cashflows: Array<{ year: number; amount: number | null }> }) => {
      const { data } = await apiClient.put<FundAssetCashflow[]>("/cashflows", payload);
      return data;
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["cashflows", vars.fund_asset_id] });
      qc.invalidateQueries({ queryKey: ["cashflows", "all"] });
      qc.invalidateQueries({ queryKey: ["fund_assets"] });
    },
  });
}
